import {
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
} from "@angular/core"
import { Observable, Subject } from "rxjs"
import { PremiumHomePage } from "../../../models/collection/premiumHomePage.model"
import { PremiumSectionPage } from "../../../models/collection/premiumSectionPage.model"
import { ITSImage } from "../../../models/itsImage.model"
import { UserService } from "../../../services/user.service"
import { IWindowResizeEvent, WindowSizeService } from "../../../services/window-size.service"
import { ModalService } from "../../../shared-components/modal/modal.service"
import Utils from "../../../utils/utils"
import { SocialShareModalComponent } from "../../../shared-components/modals/social-share-modal/social-share-modal.component"
import { STATIC_IMAGES } from "../../../models/constants"
import { StudentGateService } from "../../../services/student-gate.service"
import { takeUntil, tap } from "rxjs/operators"
import { PreviewService } from "../../../services/preview.service"

@Component({
    selector: "app-heading",
    templateUrl: "./heading.component.html",
    styleUrls: ["./heading.component.scss"],
    standalone: false,
})
export class HeadingComponent implements OnInit, OnDestroy, OnChanges {
    private readonly heroImageAltBreakpoint: number = 1199
    private readonly backgroundGradient = `
        linear-gradient(269deg, rgba(0,0,0,0.28) 3%,
        rgba(0,0,0,0.31) 28%, rgba(0,0,0,0.45) 45%,
        rgba(0,0,0,0.61) 61%, rgba(0,0,0,0.72) 77%,
        rgba(0,0,0,0.72) 88%, rgba(0,0,0,0.72) 99%)`

    @ViewChild("container", { read: ElementRef })
    private containerRef: ElementRef
    isInPreviewMode: boolean = false
    private destroyed$: Subject<void> = new Subject<void>()

    @Input() collection: PremiumSectionPage | PremiumHomePage
    @Input() collectionSlug: string = ""
    @Input() jumpToSectionTitle: string = "Jump to Resources"
    @Input() resourcesDescription: string = ""

    heroAlt: string
    heroURL: string
    heroBackground: string
    whiteLogoAlt: string = ""
    whiteLogoURL: string = null
    colorLogoAlt: string = ""
    colorLogoURL: string = null
    googleClassroomImagePath: string
    heroImage: HTMLImageElement
    isCollectionHomePage: boolean
    toggleOptions: any[] = []
    isStudentExperience: boolean = false

    resizeObservable: Observable<IWindowResizeEvent> = this.windowSize.resize.pipe(
        takeUntil(this.destroyed$),
        tap(() => this.updateContainerVisuals())
    )

    gradeChangeObservable: Observable<string> = this.studentGateService.selectedGradeState.pipe(
        takeUntil(this.destroyed$),
        tap(() => (this.toggleOptions = this.studentGateService.collectionGradeOptions))
    )

    constructor(
        public userService: UserService,
        private modalService: ModalService,
        private renderer: Renderer2,
        public windowSize: WindowSizeService,
        private studentGateService: StudentGateService,
        private previewService: PreviewService
    ) {}

    @Input()
    public set hero(value: ITSImage) {
        this.heroAlt = value.altText
        this.heroURL = value.widthResize(1400)
        this.setHeroBackground()
    }

    @Input()
    public set whiteLogo(value: ITSImage) {
        if (value) {
            this.whiteLogoAlt = value.altText
            this.whiteLogoURL = value.resize(350, "", "png")
        } else {
            this.whiteLogoAlt = ""
            this.whiteLogoURL = null
        }
    }

    @Input()
    public set colorLogo(value: ITSImage) {
        if (value) {
            this.colorLogoAlt = value.altText
            this.colorLogoURL = value.resize(336, "", "png")
        } else {
            this.colorLogoAlt = ""
            this.colorLogoURL = null
        }
    }

    public ngOnInit() {
        this.isInPreviewMode =
            this.previewService.previewOnCollection || this.previewService.previewOnSubCollection
        this.gradeChangeObservable.subscribe()
        this.resizeObservable.subscribe()
        this.isStudentExperience = Utils.hasStudentExperience()
        this.isCollectionHomePage = this.collection instanceof PremiumHomePage
    }

    public toggleGrades(selectedOption: string) {
        this.studentGateService.setSelectedCollectionGradeOption(selectedOption)
    }

    public shareToGoogleClassroom() {
        Utils.shareLinkToGC()
    }

    public openShareModal(collectionTitle: string): void {
        this.modalService.open(SocialShareModalComponent, {
            data: {
                titleToShare: collectionTitle,
            },
        })
    }

    ngOnChanges() {
        this.setupHeroVisuals()
    }

    private setupHeroVisuals() {
        this.googleClassroomImagePath = STATIC_IMAGES.google_classroom_x48_yellow_stroke_icon
        this.heroImage = new Image()
        this.heroImage.src = this.heroURL
        this.heroImage.onload = () => {
            this.setHeroBackground()
            this.updateContainerVisuals()
        }
    }

    private updateContainerVisuals() {
        if (!this.containerRef) return
        const { nativeElement } = this.containerRef
        if (Utils.isScreenWidthLarger(this.heroImageAltBreakpoint)) {
            this.renderer.setStyle(nativeElement, "background-image", this.heroBackground)
            this.renderer.setStyle(nativeElement, "minHeight", `${this.heroImage.height}px`)
        } else {
            this.renderer.removeStyle(nativeElement, "minHeight")
            this.renderer.removeStyle(nativeElement, "background-image")
        }
    }

    private setHeroBackground() {
        this.heroBackground = this.collection.heroGradient
            ? `${this.backgroundGradient},url(${this.heroURL})`
            : `url(${this.heroURL})`
    }

    public ngOnDestroy() {
        this.destroyed$.next()
        this.destroyed$.complete()
    }
}
